import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useBestPracticesApiFacade } from '../../apis/facades/useBestPracticesApiFacade';
import {
  addBestPractice,
  deleteBestPractice,
  selectCustomBestPractices,
  selectIsLoadingBestPractices,
  selectSweepBestPractices,
  setBestPractices,
  setLoading,
  updateBestPractice,
} from '../../reducers/bestPracticesReducer';
import { BestPractice, CreateBestPractice } from './types';
import { RootState } from '../../reducers';
import { telemetry } from '../../telemetry';
import { Domain } from '@server/domain';
import { undo } from '../../reducers/undoable';

export const useBestPractices = (domain?: Domain) => {
  const dispatch = useDispatch();
  const bestPracticesApi = useBestPracticesApiFacade();
  const isLoading = useSelector(selectIsLoadingBestPractices);
  const sweepBestPractices = useSelector((state: RootState) =>
    selectSweepBestPractices(state, domain),
  );
  const customBestPractices = useSelector((state: RootState) =>
    selectCustomBestPractices(state, domain),
  );

  const fetchBestPractices = useCallback(async () => {
    try {
      dispatch(setLoading({ isLoading: true }));
      const bestPractices = await bestPracticesApi.get_bestPractices();
      dispatch(setBestPractices({ bestPractices }));
    } catch (error) {
      telemetry.captureError(error);
      dispatch(setLoading({ isLoading: false }));
    }
  }, [bestPracticesApi, dispatch]);

  const createBestPractice = useCallback(
    async (newBestPractice: CreateBestPractice, index?: number) => {
      try {
        const createdBestPractice = await bestPracticesApi.post_bestPractice(newBestPractice);
        dispatch(addBestPractice({ bestPractice: createdBestPractice, index }));
        return createdBestPractice;
      } catch (error) {
        telemetry.captureError(error);
        return null;
      }
    },
    [bestPracticesApi, dispatch],
  );

  const updateBestPracticeDetails = useCallback(
    async (
      id: string,
      bestPracticeUpdate: Partial<Pick<BestPractice, 'severity'>>,
    ) => {
      const optimisticAction = updateBestPractice({ id, update: bestPracticeUpdate });
      dispatch(optimisticAction);
      try {
        await bestPracticesApi.patch_bestPractice(id, bestPracticeUpdate);
        return true;
      } catch (error) {
        telemetry.captureError(error);
        dispatch(undo(optimisticAction));
        return false;
      }
    },
    [bestPracticesApi, dispatch],
  );

  const removeBestPractice = useCallback(
    async (id: string) => {
      const optimisticAction = deleteBestPractice({ bestPracticeId: id });
      dispatch(optimisticAction);
      try {
        await bestPracticesApi.delete_bestPractice(id);
        dispatch(optimisticAction);
        return true;
      } catch (error) {
        telemetry.captureError(error);
        dispatch(undo(optimisticAction));
        return false;
      }
    },
    [bestPracticesApi, dispatch],
  );

  const removeAndCreateBestPractice = useCallback(
    async (updatedBestPractice: BestPractice, index?: number) => {
      const optimisticAction = updateBestPractice({
        id: updatedBestPractice.id,
        update: updatedBestPractice,
      });
      dispatch(optimisticAction);
      try {
        await bestPracticesApi.delete_bestPractice(updatedBestPractice.id);
        const createdBestPractice = await bestPracticesApi.post_bestPractice(updatedBestPractice);

        //dispatch only after both apis are completed, for a smooth UX
        dispatch(deleteBestPractice({ bestPracticeId: updatedBestPractice.id }));
        dispatch(addBestPractice({ bestPractice: createdBestPractice, index }));
        return true;
      } catch (error) {
        telemetry.captureError(error);
        dispatch(undo(optimisticAction));
        return false;
      }
    },
    [bestPracticesApi, dispatch],
  );

  return {
    isLoading,
    sweepBestPractices,
    customBestPractices,
    fetchBestPractices,
    createBestPractice,
    updateBestPracticeDetails,
    removeBestPractice,
    removeAndCreateBestPractice,
  };
};
